import { z } from 'zod'

// ============================================================================
// Blocked Email Domains
// ============================================================================

/**
 * Email domains that are not allowed during signup.
 * - Test/example domains (RFC 2606 reserved)
 * - Known disposable/temporary email providers
 */
const BLOCKED_EMAIL_DOMAINS = new Set([
	// Reserved test domains (RFC 2606)
	'example.com',
	'example.org',
	'example.net',
	'example.edu',

	// Common disposable email providers
	'mailinator.com',
	'10minutemail.com',
	'guerrillamail.com',
	'guerrillamail.org',
	'guerrillamail.net',
	'guerrillamail.biz',
	'guerrillamail.de',
	'guerrillamail.net',
	'tempmail.org',
	'temp-mail.org',
	'throwaway.email',
	'disposable.email',
	'fakeinbox.com',
	'maildrop.cc',
	'getairmail.com',
	'yopmail.com',
])

/**
 * Checks if an email domain is blocked.
 * Used to prevent bot signups and disposable email abuse.
 */
export function isBlockedEmailDomain(email: string): {
	blocked: boolean
	reason: 'example' | 'disposable' | null
} {
	const domain = email.split('@')[1]?.toLowerCase()
	if (!domain) return { blocked: false, reason: null }

	if (domain.startsWith('example.')) {
		return { blocked: true, reason: 'example' }
	}

	if (BLOCKED_EMAIL_DOMAINS.has(domain)) {
		return { blocked: true, reason: 'disposable' }
	}

	return { blocked: false, reason: null }
}

// ============================================================================
// Validation Schemas
// ============================================================================

export const USERNAME_MIN_LENGTH = 3
export const USERNAME_MAX_LENGTH = 20
export const MINIMUM_AGE = 13

export const UsernameSchema = z
	.string({ required_error: 'Username is required' })
	.min(USERNAME_MIN_LENGTH, { message: 'Username is too short' })
	.max(USERNAME_MAX_LENGTH, { message: 'Username is too long' })
	.regex(/^[a-zA-Z0-9_]+$/, {
		message: 'Username can only include letters, numbers, and underscores',
	})
	// users can type the username in any case, but we store it in lowercase
	.transform((value) => value.toLowerCase())

export const PasswordSchema = z
	.string({ required_error: 'Password is required' })
	.min(6, { message: 'Password is too short' })
	// NOTE: bcrypt has a limit of 72 bytes (which should be plenty long)
	// https://github.com/epicweb-dev/epic-stack/issues/918
	.refine((val) => new TextEncoder().encode(val).length <= 72, {
		message: 'Password is too long',
	})

export const NameSchema = z
	.string({ required_error: 'Name is required' })
	.min(3, { message: 'Name is too short' })
	.max(40, { message: 'Name is too long' })

export const GenderOptions = ['male', 'female', 'prefer_not_to_say'] as const

export const GenderSchema = z.enum(GenderOptions, {
	required_error: 'Gender is required',
})

export const OptionalGenderSchema = z.preprocess(
	(value) => (value === '' || value === null ? undefined : value),
	GenderSchema.optional(),
)

export const AgeSchema = z.preprocess(
	(value) => (value === '' || value === null ? undefined : value),
	z.coerce
		.number({
			required_error: 'Age is required',
			invalid_type_error: 'Age is required',
		})
		.int({ message: 'Age must be a whole number' })
		.min(MINIMUM_AGE, {
			message: `You must be at least ${MINIMUM_AGE} years old`,
		})
		.max(120, { message: 'Please enter a valid age' }),
)

export const OptionalAgeSchema = z.preprocess(
	(value) => (value === '' || value === null ? undefined : value),
	z.coerce
		.number({ invalid_type_error: 'Age must be a number' })
		.int({ message: 'Age must be a whole number' })
		.min(MINIMUM_AGE, {
			message: `You must be at least ${MINIMUM_AGE} years old`,
		})
		.max(120, { message: 'Please enter a valid age' })
		.optional(),
)

export const EmailSchema = z
	.string({ required_error: 'Email is required' })
	.email({ message: 'Email is invalid' })
	.min(3, { message: 'Email is too short' })
	.max(100, { message: 'Email is too long' })
	.refine(
		(email) => {
			const { blocked, reason } = isBlockedEmailDomain(email)
			return !blocked
		},
		(email) => {
			const { reason } = isBlockedEmailDomain(email)
			return {
				message:
					reason === 'example'
						? 'Email addresses from example.com are not allowed'
						: 'Disposable email addresses are not allowed',
			}
		},
	)
	// users can type the email in any case, but we store it in lowercase
	.transform((value) => value.toLowerCase())

export const PasswordAndConfirmPasswordSchema = z
	.object({ password: PasswordSchema, confirmPassword: PasswordSchema })
	.superRefine(({ confirmPassword, password }, ctx) => {
		if (confirmPassword !== password) {
			ctx.addIssue({
				path: ['confirmPassword'],
				code: 'custom',
				message: 'The passwords must match',
			})
		}
	})
